import { Alert, Box, CircularProgress, Paper, Snackbar, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { ArticleActionBar } from '../components/ArticleActionBar'
import { ArticleMetadataSections } from '../components/ArticleMetadataSections'
import { ArticleResourcesSection } from '../components/ArticleResourcesSection'
import { ArticleStatusChip } from '../components/ArticleStatusChip'
import { ArticleTechnicalSection } from '../components/ArticleTechnicalSection'
import { ArticleWorkflowSection } from '../components/ArticleWorkflowSection'
import { useArticleForm } from '../hooks/useArticleForm'
import { DropdownOptionRequestDialog } from '../../dropdown/components/DropdownOptionRequestDialog'

export function ArticleFormPage() {
  const navigate = useNavigate()
  const {
    autoTags,
    articleStatus,
    currentUserRole,
    deleteExistingAttachment,
    dropdownOptions,
    errors,
    existingAttachments,
    existingInlineImageLabels,
    files,
    form,
    handleInlineImagePaste,
    handleRequestOption,
    hasRecoveryDraft,
    images,
    inlineLinkedImageLabels,
    isCustomerSpecific,
    isEditing,
    loading,
    openRequestDialog,
    renameLinkedInlineImage,
    removeTag,
    requestDialog,
    requestForm,
    saveDraft,
    saving,
    setField,
    setFiles,
    setImages,
    setRichTextField,
    setRequestDialog,
    setRequestForm,
    setSnackbar,
    setTagInput,
    snackbar,
    submitForReview,
    submitting,
    tagInput,
    workflowUsers,
    addTag,
    addTroubleshootingRow,
    updateTroubleshootingRow,
    removeTroubleshootingRow,
    uploadStatuses,
  } = useArticleForm()

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box component="form" onSubmit={event => {
      event.preventDefault()
      void saveDraft()
    }} display="flex" flexDirection="column" gap={3}>
      <Paper sx={{ p: 3, borderLeft: '4px solid #0f4c81' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5">{isEditing ? 'Edit Technote' : 'Create New Technote'}</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Use this form to register a new technical note for troubleshooting, procedures, lessons learned, or configuration references.
            </Typography>
          </Box>
          <Box>
            <ArticleStatusChip state={isEditing ? articleStatus : 'draft'} />
          </Box>
        </Box>
      </Paper>

      <ArticleMetadataSections
        dropdownOptions={dropdownOptions}
        errors={errors}
        form={form}
        isCustomerSpecific={isCustomerSpecific}
        onRequestOption={openRequestDialog}
        setField={setField}
        status={isEditing ? articleStatus : 'draft'}
      />

      <ArticleTechnicalSection
        errors={errors}
        form={form}
        setField={setField}
        setRichTextField={setRichTextField}
        handleInlineImagePaste={handleInlineImagePaste}
        addTroubleshootingRow={addTroubleshootingRow}
        updateTroubleshootingRow={updateTroubleshootingRow}
        removeTroubleshootingRow={removeTroubleshootingRow}
      />

      <ArticleResourcesSection
        autoTags={autoTags}
        deleteExistingAttachment={deleteExistingAttachment}
        existingAttachments={existingAttachments}
        existingInlineLinkedImageLabels={existingInlineImageLabels}
        files={files}
        form={form}
        images={images}
        inlineLinkedImageLabels={inlineLinkedImageLabels}
        onRenameLinkedImage={renameLinkedInlineImage}
        removeTag={removeTag}
        setField={setField}
        setFiles={setFiles}
        setImages={setImages}
        setTagInput={setTagInput}
        tagInput={tagInput}
        addTag={addTag}
        uploadStatuses={uploadStatuses}
      />

      <ArticleWorkflowSection
        currentUserRole={currentUserRole}
        form={form}
        isEditing={isEditing}
        setField={setField}
        workflowUsers={workflowUsers}
      />

      <ArticleActionBar
        hasRecoveryDraft={hasRecoveryDraft}
        isEditing={isEditing}
        saving={saving}
        submitting={submitting}
        onCancel={() => navigate('/')}
        onSaveDraft={() => void saveDraft()}
        onSubmitForReview={() => void submitForReview()}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(current => ({ ...current, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <DropdownOptionRequestDialog
        requestDialog={requestDialog}
        requestForm={requestForm}
        onClose={() => setRequestDialog({ open: false, fieldName: '', label: '' })}
        onSubmit={handleRequestOption}
        setRequestForm={setRequestForm}
      />
    </Box>
  )
}
